import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { X, Archive, FolderClock, CheckCircle, AlertTriangle, Loader } from 'lucide-react';
import styles from './ChangeStatusModal.module.css';
import { updateCaseStatus } from '../../../../../services/clienteService';
import { ClienteCaso, EstadoCaso } from '../../../../../interfaces/cliente.types';

interface ChangeStatusModalProps {
    onClose: () => void;
    onStatusUpdated: () => void;
    caso: ClienteCaso;
}

export const ChangeStatusModal: React.FC<ChangeStatusModalProps> = ({ onClose, onStatusUpdated, caso }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpdateStatus = async (nuevoEstado: EstadoCaso) => {
        if (nuevoEstado === 'Cerrado' && caso.saldo_pendiente > 0) {
            if (!window.confirm(`Este caso tiene un saldo pendiente de S/ ${caso.saldo_pendiente.toFixed(2)}. ¿Está seguro de que desea cerrarlo de todas formas?`)) {
                return;
            }
        }

        setIsLoading(true);
        setError(null);
        try {
            await updateCaseStatus(caso.id, nuevoEstado);
            onStatusUpdated();
            onClose();
        } catch (err: any) {
            setError(err.message || 'No se pudo actualizar el estado.');
        } finally {
            setIsLoading(false);
        }
    };

    const modalVariants = {
        hidden: { scale: 0.9, opacity: 0 },
        visible: { scale: 1, opacity: 1 },
        exit: { scale: 0.9, opacity: 0 }
    };

    const renderActions = () => {
        if (isLoading) {
            return <div className={styles.loadingWrapper}><Loader className={styles.spinner} /><span>Actualizando...</span></div>;
        }

        switch (caso.estado_caso) {
            case 'Activo':
                return (
                    <>
                        <button className={styles.actionButton} onClick={() => handleUpdateStatus('En Archivo')}>
                            <Archive size={20} /> Archivar Caso
                        </button>
                        <button className={`${styles.actionButton} ${styles.closeButton}`} onClick={() => handleUpdateStatus('Cerrado')}>
                            <FolderClock size={20} /> Finalizar y Cerrar
                        </button>
                    </>
                );
            case 'En Archivo':
                return (
                    <button className={`${styles.actionButton} ${styles.reactivateButton}`} onClick={() => handleUpdateStatus('Activo')}>
                        <CheckCircle size={20} /> Reactivar Caso
                    </button>
                );
            case 'Cerrado':
                return (
                     <button className={`${styles.actionButton} ${styles.reactivateButton}`} onClick={() => handleUpdateStatus('Activo')}>
                        <CheckCircle size={20} /> Reabrir Caso
                    </button>
                );
            default:
                return null;
        }
    };

    return (
        <div className={styles.modalOverlay} onClick={onClose}>
            <motion.div 
                className={styles.modalContent}
                variants={modalVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
                onClick={(e) => e.stopPropagation()}
            >
                <button onClick={onClose} className={styles.closeIcon}><X size={24} /></button>
                <header className={styles.modalHeader}>
                    <h2 className={styles.title}>Cambiar Estado del Caso</h2>
                    <p className={styles.subtitle}>Expediente: {caso.numero_expediente || 'N/A'} ({caso.nombre_completo})</p>
                </header>
                <div className={styles.currentStateWrapper}>
                    <span className={styles.currentStatusLabel}>Estado Actual:</span>
                    <span className={`${styles.statusBadge} ${styles[`status${caso.estado_caso.replace(' ', '')}`]}`}>
                        {caso.estado_caso}
                    </span>
                </div>
                <div className={styles.actionsContainer}>
                    <span className={styles.actionsLabel}>Seleccione una nueva acción:</span>
                    <div className={styles.buttonGroup}>
                        {renderActions()}
                    </div>
                </div>
                {error && 
                    <div className={styles.errorMessage}>
                        <AlertTriangle size={18} />
                        <span>{error}</span>
                    </div>
                }
            </motion.div>
        </div>
    );
};